// @flow
import type {Config} from 'bfu-auth-client/src/login/data/Config.js';

// the style is part of the JS to not require a second round trip for the loading app to get the css
// the login js file is already cached quite heavily
// this was added in v3 of login js

export const appendCSS = (config: Config): void => {
    const {color, colorLighter} = config.app;
    const css = createCSS({color, colorLighter});
    const style = document.createElement('style');
    style.type = 'text/css';
    style.appendChild(document.createTextNode(css));
    document.head.appendChild(style);
};

const createCSS = ({color, colorLighter}) => `
.BlueflagAuthForm {
    margin: 3rem auto;
    padding: 1rem;
    width: 25rem
}

.BlueflagAuthFormLogo {
    display: block;
    max-width: 100%;
    margin-bottom: 2rem
}

.BlueflagAuthFormButton {
    background-color: #fff;
    border-radius: 4px;
    color: #333;
    cursor: pointer;
    display: inline-block;
    font-size: .9rem;
    outline: 0;
    padding: .15rem .75rem;
    position: relative;
    text-align: center;
    top: 0px;
    transition: top 0s;
    background-color: ${color};
    border-color: ${color};
    color: #fff;
    margin: 1rem 0;
    padding: .5rem 1rem;
    width: 100%
}

.BlueflagAuthFormButton:focus, .BlueflagAuthFormButton:hover {
    color: ${colorLighter};
    box-shadow: none;
    outline: 0
}

.BlueflagAuthFormButton:active {
    opacity: 1;
    top: 1px
}

.BlueflagAuthFormButton-sizeKilo {
    font-size: 1rem;
    padding: .5rem 1rem .65rem;
    transition: top .05s ease-out
}

.BlueflagAuthFormButton-withIcon {
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: center;
}

.BlueflagAuthFormButton-sizeKilo:active {
    top: 2px;
    transition: top 0s ease-out
}

.BlueflagAuthFormButton-sizeMega {
    font-size: 1.2rem;
    padding: .7rem 1.5rem .9rem;
    text-transform: none;
    transition: top .05s ease-out
}

.BlueflagAuthFormButton-sizeMega:active {
    top: 2px;
    transition: top 0s ease-out
}

.BlueflagAuthFormButton-clear {
    background-color: transparent;
    color: currentColor
}

.BlueflagAuthFormButton-clear:hover, .BlueflagAuthFormButton-clear:focus {
    background-color: transparent;
    opacity: .5
}

.BlueflagAuthFormButton-primary {
    background-color: ${color};
    border-color: #135fa1;
    color: #fff
}

.BlueflagAuthFormButton-primary:hover, .BlueflagAuthFormButton-primary:focus {
    background-color: ${colorLighter};
    color: #fff
}

.BlueflagAuthFormButton-primary:active {
    background-color: #125793;
    border-color: transparent
}

.BlueflagAuthFormButton-secondary {
    background-color: #f0f0f0;
    border-color: #e6e6e6;
    color: #414141
}

.BlueflagAuthFormButton-secondary:hover, .BlueflagAuthFormButton-secondary:focus {
    background-color: #f7f7f7;
    color: #414141
}

.BlueflagAuthFormButton-secondary:active {
    background-color: #e8e8e8;
    border-color: transparent
}

.BlueflagAuthFormButton-solo {
    display: block;
    width: 100%;
    margin-bottom: .25rem
}

.BlueflagAuthFormButton-disabled {
    color: #ddd;
    background-color: #fff;
    border-color: #fff;
    cursor: not-allowed
}

.BlueflagAuthFormButton-disabled:hover, .BlueflagAuthFormButton-disabled:focus {
    color: #ddd
}

.BlueflagAuthFormButton-disabled:active {
    box-shadow: none;
    opacity: 1;
    top: 0
}

.BlueflagAuthFormButton-inline {
    margin-right: .5rem;
    margin-bottom: .5rem
}

.BlueflagAuthFormButton-paddingMilli {
    padding: .15rem .25rem
}

.BlueflagAuthFormButton:hover, .BlueflagAuthFormButton:focus {
    background-color: ${colorLighter};
    border-color: ${color};
    color: #fff
}

.BlueflagAuthFormButton:active {
    background-color: ${color};
}

.BlueflagAuthFormInput {
    background-color: #fff;
    border-color: #ddd;
    border-radius: 2px;
    border-style: solid;
    border-width: 1px;
    color: inherit;
    cursor: pointer;
    display: inline-block;
    font: inherit;
    font-size: 16px;
    line-height: 1.6rem;
    min-height: 2rem;
    outline: 0;
    padding: .25rem .5rem;
    width: 100%
}

.BlueflagAuthFormInput:focus, .BlueflagAuthFormInput:hover {
    border-color: #07569a;
    outline: 0
}

.BlueflagAuthFormInput:focus-positive, .BlueflagAuthFormInput:hover-positive {
    border-color: #12BC4E
}

.BlueflagAuthFormInput:focus-negative, .BlueflagAuthFormInput:hover-negative {
    border-color: #e85656
}

.BlueflagAuthFormInput-disabled {
    border-color: #fff;
    cursor: default
}

.BlueflagAuthFormInput-disabled:focus, .BlueflagAuthFormInput-disabled:hover {
    border-color: #fff
}

.BlueflagAuthFormInput::-webkit-input-placeholder {
    color: #C3C3C3
}

.BlueflagAuthFormInput:-ms-input-placeholder {
    color: #C3C3C3
}

.BlueflagAuthFormInput::-moz-placeholder {
    color: #C3C3C3
}

.BlueflagAuthFormInput-positive {
    border-color: #12BC4E
}

.BlueflagAuthFormInput-negative {
    border-color: #e85656
}

.BlueflagAuthFormLabel {
    color: #666;
    display: block;
    font-size: .9em;
    font-weight: 500;
    line-height: 1.6rem;
    margin: .25rem 0
}

.BlueflagAuthFormLoader {
    display: inline-block;
    font-size: 2rem;
    line-height: 2.3rem
}

.BlueflagAuthFormLoader_spinner {
    height: 1em;
    width: 1em;
    text-indent: -9999em;
    margin: auto;
    border: .06em solid transparent;
    border-radius: 100%;
    border-top-color: #d9d9d9;
    -webkit-animation: Loader 2.5s infinite ease;
    animation: Loader 2.5s infinite ease
}

.BlueflagAuthFormLoader-sizeTera {
    font-size: 3rem;
    line-height: 6rem
}

.BlueflagAuthFormLoader-sizeGiga {
    font-size: 2rem;
    line-height: 2.3rem
}

.BlueflagAuthFormLoader-sizeMega {
    font-size: 1.5rem;
    line-height: 1.7rem
}

.BlueflagAuthFormLoader-sizeKilo {
    font-size: 1.3rem;
    line-height: 1.8rem
}

.BlueflagAuthFormLoader-center {
    display: block;
    padding: 3rem;
    text-align: center
}

.BlueflagAuthFormLoader_text {
    margin-top: 1rem
}

.BlueflagAuthFormLoader_text {
    font-size: 1rem
}

@keyframes Loader {
    0% {
        transform: rotate(0deg)
    }

    100% {
        transform: rotate(1080deg)
    }
}

.BlueflagAuthFormLink {
    color: ${colorLighter};
    cursor: pointer
}

.BlueflagAuthFormLink:hover, .BlueflagAuthFormLink:focus {
    color: ${colorLighter};
    text-decoration: underline
}

.BlueflagAuthFormLink-weightMilli {
    color: #838383
}

.BlueflagAuthFormLink-weightMilli:hover, .BlueflagAuthFormLink-weightMilli:focus {
    color: #838383
}

.BlueflagAuthFormLink-colorPrimary {
    color: ${color};
    font-weight: 500
}

.BlueflagAuthFormLink-colorPrimary:hover, .BlueflagAuthFormLink-colorPrimary:focus {
    color: ${color};
}

.BlueflagAuthFormLink-colorCopy {
    color: #606060
}

.BlueflagAuthFormLink-ellipsis {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding-right: .5rem
}

.BlueflagSignInViewWrapper {
    padding-top: 120px;
    display: flex;
    align-items: center;
    gap: 60px;
    justify-content: center;
}

@media only screen and (max-width: 500px) {
    .BlueflagSignInViewWrapper {
        flex-direction: column;
    }
}

.BlueflagAuthFormVerticalDivider {
    position: relative;
    transform: translate(-50%);
    border: 2px solid rgb(240, 240, 240);
    height: 370px;
}

@media only screen and (max-width: 650px) {
    .BlueflagAuthFormVerticalDivider {
        width: 80vw;
        height: 1px;
        transform: none;
    }
}

.BlueflagAuthFormVerticalDividerInner {
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    background: white;
    fontWeight: bold;
    padding: 8px 10px;
}


@media only screen and (max-width: 650px) {
    .BlueflagAuthFormVerticalDividerInner {
        left: 50%;
    }
}

.BlueflagAuthFormLink--Button {
    display: inline-block;
    height: 2rem;
    line-height: 1.2;
    padding: .5rem;
    text-align: center;
    vertical-align: top
}

.BlueflagAuthFormLink.BlueflagAuthFormLink-option {
    display: block;
    text-align: left
}

.BlueflagAuthFormMessage {
    -webkit-animation: .3s ease-out onShow;
    animation: .3s ease-out onShow;
    background-color: #f7f7f7;
    color: gray;
    opacity: 1;
    padding: 1rem;
    position: relative;
    top: 0;
    margin-bottom: 1rem
}

.BlueflagAuthFormMessage-positive {
    background-color: #DDFBE5;
    color: #12BC4E
}

.BlueflagAuthFormMessage-negative {
    background-color: #fbe9e9;
    color: #e85656
}

.BlueflagAuthFormMessage-warning {
    background-color: #FFF1DF;
    color: #C8820F
}

.BlueflagAuthFormMessage-margin {
    margin-bottom: 1rem
}

@keyframes onShow {
    0% {
        opacity: 0;
        top: .25rem
    }

    100% {
        opacity: 1;
        top: 0
    }
}

.BlueflagAuthFormText-sizeTera {
    color: #333;
    font-size: 4rem;
    letter-spacing: 0;
    line-height: 6rem
}

.BlueflagAuthFormText-sizeGiga {
    color: #333;
    font-size: 2.4rem;
    letter-spacing: 0;
    line-height: 2.9rem
}

.BlueflagAuthFormText-sizeMega {
    color: #333;
    font-size: 1.5rem;
    letter-spacing: 0;
    line-height: 1.7rem
}

.BlueflagAuthFormText-sizeKilo {
    color: #333;
    font-size: 1.3rem;
    letter-spacing: 0;
    line-height: 1.8rem
}

.BlueflagAuthFormText-sizeHecto {
    color: #333;
    font-size: 1.1rem;
    letter-spacing: 0
}

.BlueflagAuthFormText-sizeMilli {
    font-size: .8rem
}

.BlueflagAuthFormText-marginGiga {
    margin-bottom: 2rem
}

.BlueflagAuthFormText-marginMega {
    margin-bottom: 1.5rem
}

.BlueflagAuthFormText-marginKilo {
    margin-bottom: 1.5rem
}

.BlueflagAuthFormText-marginHecto {
    margin-bottom: 1.5rem
}

.BlueflagAuthFormText-margin {
    margin-bottom: 1.5rem
}

.BlueflagAuthFormText-marginMilli {
    margin-bottom: .5rem
}

.BlueflagAuthFormText-marginLeft {
    margin-left: 1rem
}

.BlueflagAuthFormText-marginLeftMilli {
    margin-left: .5rem
}

.BlueflagAuthFormText-marginRight {
    margin-right: 1rem
}

.BlueflagAuthFormText-marginRightMilli {
    margin-right: .5rem
}

.BlueflagAuthFormText-weightMilli {
    color: #838383
}

.BlueflagAuthFormText-emphasis {
    font-style: italic
}

.BlueflagAuthFormText-weightKilo {
    font-weight: 700
}

.BlueflagAuthFormText-positive {
    color: #12BC4E
}

.BlueflagAuthFormText-negative {
    color: #e85656
}

.BlueflagAuthFormText-link {
    color: ${colorLighter};
    cursor: pointer
}

.BlueflagAuthFormText-link:hover, .BlueflagAuthFormText-link:focus {
    color: ${colorLighter};
    text-decoration: underline
}

.BlueflagAuthFormText-capitalize {
    text-transform: capitalize
}

.BlueflagAuthFormText-monospace {
    font-family: Menlo, Consolas, monospace
}

.BlueflagAuthFormText-block {
    display: block
}

.BlueflagAuthFormText-block:after {
    clear: both;
    content: '';
    display: table
}

.BlueflagAuthFormText-relative {
    position: relative
}

.BlueflagAuthFormText-active {
    text-decoration: underline
}

.BlueflagAuthFormText-left {
    text-align: left
}

.BlueflagAuthFormText-center {
    text-align: center
}

.BlueflagAuthFormText-right {
    text-align: right
}

.BlueflagAuthFormText-pointer {
    cursor: pointer
}

.BlueflagAuthFormText-disabled {
    color: #999;
    cursor: default
}

.BlueflagAuthFormText-nowrap {
    white-space: nowrap
}

.BlueflagAuthFormText-onImage {
    color: #fff;
    text-shadow: 0 0 2px rgba(0, 0, 0, 0.8);
    font-weight: 700
}

.BlueflagAuthFormText-ellipsis {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding-right: .5rem
}

.BlueflagAuthFormText-navigation {
    line-height: 2rem;
    min-height: 2rem
}
`;
